import { createAxios } from 'util/create_axios'

import { createConsumer } from '@rails/actioncable'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'
import { createCableCarRoute } from 'redux-cablecar'
import trix from 'trix'

import {
  SELECTED_AUDIO_DEVICE,
  SELECTED_AUDIO_OUTPUT_DEVICE,
  SELECTED_VIDEO_DEVICE,
} from '../../components/calls/tracks/use_local_tracks'
import { ActionCableConsumerContext } from '../../contexts/action_cable_consumer_context'
import { AxiosContext } from '../../contexts/axios_context'
import { APP_LOCATION_CACHE_LS_KEY } from '../../hooks/use_app_location_state_manager'
import { REDUX_STORES_LS_KEY } from '../../hooks/use_persist_redux_stores'
import { LiveChatApp } from '../../pages/live_chat/live_chat_app'
import {
  createLiveChatAccount,
  fetchLiveChatAccount,
} from '../../reduxes/slices/accounts/actions'
import { setActive } from '../../reduxes/slices/actives/actions'
import { setFetched } from '../../reduxes/slices/api/actions'
import {
  cableCarDisconnected,
  loadCableCar,
} from '../../reduxes/slices/cablecar/actions'
import { setCurrent } from '../../reduxes/slices/current/actions'
import {
  invalidateAll,
  setComponentInvalid,
  setComponentValid,
} from '../../reduxes/slices/invalid/actions'
import { setSelected } from '../../reduxes/slices/selected/actions'
import RootStoreContext from '../../reduxes/stores/root_store/context'
import { createRootStore } from '../../reduxes/stores/root_store/create'
import { createSubStore } from '../../reduxes/stores/sub_store/create'
import liteApiRoutes from '../../routes/lite_api_routes.json'

trix // prevent it from complaining

const KEY = 'local_storage_invalidates'
const LOCAL_STORAGE_KEYS = [
  APP_LOCATION_CACHE_LS_KEY,
  REDUX_STORES_LS_KEY,
  SELECTED_AUDIO_DEVICE,
  SELECTED_VIDEO_DEVICE,
  SELECTED_AUDIO_OUTPUT_DEVICE,
]

// manually copy and paste Date.now() from console to invalidate
const INVALIDATES = {
  all: 1668679963725, // remove all local storage keys
  // the keys below here are nested under the main app local storage key
  [APP_LOCATION_CACHE_LS_KEY]: 1668679963725,
  [REDUX_STORES_LS_KEY]: 1668679963725,
  [SELECTED_AUDIO_DEVICE]: 1668679963725,
  [SELECTED_VIDEO_DEVICE]: 1668679963725,
  [SELECTED_AUDIO_OUTPUT_DEVICE]: 1668679963725,
}

run()

async function run() {
  handleLocalStorageInvalidates()

  const environment = window.preloadData.rootStore.current.environment
  const liveChatAppId = window.preloadData.rootStore.liveChatAppId
  const jwtLsId = `live_chat_jwt_${environment}_${liveChatAppId}`

  window.speakeasyData = {
    liveChatApp: true,
    jwtLsId,
  }

  const axios = createAxios(
    {
      baseURL: window.speakeasySettings.apiBase,
    },
    true,
  )
  const jwt = localStorage.getItem(jwtLsId)
  if (jwt) {
    axios.defaults.headers.common['AUTHORIZATION'] = `Bearer ${jwt}`
    const result = await fetchLiveChatAccount(axios)
    axios.defaults.baseURL =
      window.speakeasySettings.apiBase +
      liteApiRoutes.lite_api_v1_member_root.path.replace(
        ':current_member_id',
        result.data.preload_data.rootStore.current.memberId,
      )
    window.preloadData = result.data.preload_data
  }

  const { preloadDataFromServer, preloadDataFromLS } = getPreloadData()

  const rootStorePreloadData = {
    ...preloadDataFromLS.rootStore,
    ...preloadDataFromServer.rootStore,
    liveChatApp: true,
  }

  const { wsURL: webSocketURL, wsURLAlt: webSocketURLAlt } =
    rootStorePreloadData.current || {}
  const ActionCableConsumer = webSocketURL
    ? createConsumer(`${webSocketURL}?access=widget`)
    : createConsumer()
  const USER_CHANNEL_NAME = 'UserChannel'
  const CABLE_CAR_ROUTE_OPTIONS = webSocketURL
    ? {
        webSocketURL: `${webSocketURL}?access=widget`,
        consumer: ActionCableConsumer,
      }
    : { consumer: ActionCableConsumer }

  const rootStoreCableCarRoute = createCableCarRoute(CABLE_CAR_ROUTE_OPTIONS)
  const rootStoreCableCarMiddleware = rootStoreCableCarRoute.createMiddleware()
  const rootStore = createRootStore(rootStorePreloadData, [
    rootStoreCableCarMiddleware,
  ])

  const cableCarRoute = createCableCarRoute(CABLE_CAR_ROUTE_OPTIONS)
  const cableCarMiddleware = cableCarRoute.createMiddleware()
  const storePreloadDataFromServer = Object.values(
    preloadDataFromServer.subStores,
  )[0]
  const store = createSubStore(
    {
      ...(Object.values(preloadDataFromServer.subStores)[0] || {}),
      ...(Object.values(preloadDataFromLS.subStores)[0] || {}),
    },
    [cableCarMiddleware],
  )
  if (storePreloadDataFromServer) {
    const { api, current, selected, actives } = storePreloadDataFromServer
    store.dispatch(setCurrent(current))
    store.dispatch(setFetched('preload data', api))
    store.dispatch(setSelected(selected))
    if (actives) {
      store.dispatch(setActive('actives', actives, true))
    }
  }

  const ROOT_STORE_CABLECAR_OPTIONS = {
    disconnected: handleCableCarDisconnect,
    connected: handleCableCarConnect,
    permittedReceiveActions: (action) => {
      return !action.payload?.subsidiaryStoreId
    },
  }

  const connectCablecarInstance = () => {
    const rootStoreCableCar = rootStoreCableCarRoute.connect(
      rootStore,
      USER_CHANNEL_NAME,
      {
        ...ROOT_STORE_CABLECAR_OPTIONS,
        params: {
          live_chat_jwt: localStorage.getItem(window.speakeasyData.jwtLsId),
        },
      },
    )
    rootStore.dispatch(loadCableCar(rootStoreCableCar))
    cableCarRoute.connect(store, USER_CHANNEL_NAME, {
      permittedReceiveActions: (action) => {
        const memberId = store.getState().current.memberId
        return memberId && action.payload?.subsidiaryStoreId === memberId
      },
      params: {
        live_chat_jwt: localStorage.getItem(window.speakeasyData.jwtLsId),
      },
    })
  }

  axios.interceptors.request.use(async function (config) {
    if (!rootStore.getState().current.userId) {
      try {
        const updatedAxiosConfig = await createLiveChatAccount(
          axios,
          rootStore.dispatch,
          store.dispatch,
        )
        return {
          ...config,
          ...updatedAxiosConfig,
          headers: {
            ...config.headers,
            ...updatedAxiosConfig.headers,
            common: {
              ...config.headers.common,
              ...updatedAxiosConfig.headers.common,
            },
          },
        }
      } catch (e) {
        return config
      }
    } else {
      return config
    }
  })

  const container = document.getElementById('yield-block')
  const root = createRoot(container)
  root.render(
    <Provider store={rootStore} context={RootStoreContext}>
      <Provider store={store}>
        <BrowserRouter>
          <ActionCableConsumerContext.Provider value={ActionCableConsumer}>
            <AxiosContext.Provider value={axios}>
              <LiveChatApp
                actionCableConsumer={ActionCableConsumer}
                connectCablecarInstance={connectCablecarInstance}
                rootStore={rootStore}
                subStore={store}
              />
            </AxiosContext.Provider>
          </ActionCableConsumerContext.Provider>
        </BrowserRouter>
      </Provider>
    </Provider>,
  )

  function handleCableCarConnect() {
    // eslint-disable-next-line no-console
    console.log('%cWEBSOCKET CONNECTED', 'color:green;')
    if (rootStore.getState().invalid.cablecar) {
      rootStore.dispatch(setComponentValid('cablecar'))
      rootStore.dispatch(invalidateAll())
      store.dispatch(invalidateAll())
    }
  }

  function handleCableCarDisconnect() {
    // eslint-disable-next-line no-console
    console.log('%cWEBSOCKET DISCONNECTED', 'color:red;')
    rootStore.dispatch(cableCarDisconnected())
    store.dispatch(setComponentInvalid('typing_indicators'))
  }
}

function getPreloadData() {
  const defaultPreloadData = {
    rootStore: {},
    subStores: {},
  }
  return {
    preloadDataFromServer: window.preloadData || defaultPreloadData,
    preloadDataFromLS:
      JSON.parse(
        window.localStorage.getItem(
          `speakeasy_widget_${window.preloadData.rootStore.liveChatAppId}`,
        ) || '{}',
      )[REDUX_STORES_LS_KEY] || defaultPreloadData,
  }
}

function handleLocalStorageInvalidates() {
  const id = `speakeasy_widget_${window.preloadData.rootStore.liveChatAppId}`
  const ls = JSON.parse(window.localStorage.getItem(id) || '{}')
  const invalidates = JSON.parse(window.localStorage.getItem(KEY))
  if (!invalidates?.all || invalidates.all !== INVALIDATES.all) {
    Object.keys(localStorage).forEach((key) => {
      if (key !== KEY) {
        window.localStorage.removeItem(key)
      }
    })
  } else {
    LOCAL_STORAGE_KEYS.forEach((key) => {
      const invalidate = INVALIDATES[key]
      const lastInvalidate = invalidates?.[key]
      if (!lastInvalidate || lastInvalidate !== invalidate) {
        delete ls[key]
      }
    })
    window.localStorage.setItem(id, JSON.stringify(ls))
  }
  window.localStorage.setItem(KEY, JSON.stringify(INVALIDATES))
}
